// components/public/PublicJobPage.tsx
// #265 — the PUBLIC, no-auth job-posting page. An employer shares a live posting
// via `${origin}/jobb/:id` and anyone (logged out or in) lands here without the
// dashboard shell. Reads the public endpoint (active postings only); paused/closed/
// removed postings 404 → a graceful "not available" state instead of a redirect.
//
// Renders through the SAME shared <NeedView> as the candidate + employer detail
// pages (via publicNeedToView), so the posting looks identical everywhere.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Send, ArrowRight, Building2 } from 'lucide-react';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import { PublicNeed } from '@/lib/models/jobApplication';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingState } from '@/components/custom-ui/empty-state';
import { ActionButton } from '@/components/custom-ui/action-button';
import NeedView, { publicNeedToView } from '@/components/needs/NeedView';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

// Canonical Nexilink wordmark (same asset the sidebar uses).
const LOGO_FULL_SRC =
    'https://cdn.builder.io/api/v1/image/assets/89bc6265b1a84995b7b85b1453a49e93/e937596a79e27aedd1ca37344ef2209cd6e8c5da2cc900e6c0f5467b6176325e?apiKey=89bc6265b1a84995b7b85b1453a49e93&';

type LoadState = 'loading' | 'ok' | 'unavailable';

const PublicShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="min-h-screen bg-[#f6f8f8] flex flex-col">
        {/* Minimal public header — just the brand, linking home. NO dashboard chrome. */}
        <header className="w-full border-b border-gray-100 bg-white/80 backdrop-blur-sm">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center">
                <Link to="/" aria-label="Till Nexilink">
                    <img src={LOGO_FULL_SRC} alt="Nexilink" className="h-7 w-auto" />
                </Link>
            </div>
        </header>
        <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-8">{children}</main>
        <footer className="w-full border-t border-gray-100 py-6">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-400">
                <span>Publicerad via Nexilink</span>
                <Link to="/" className="text-[#1A4D4F] font-medium hover:underline">Om Nexilink</Link>
            </div>
        </footer>
    </div>
);

const PublicJobPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { tokens, user } = useAuth();

    const [need, setNeed] = useState<PublicNeed | null>(null);
    const [state, setState] = useState<LoadState>('loading');

    useEffect(() => {
        if (!id) { setState('unavailable'); return; }
        let alive = true;
        (async () => {
            try {
                const data = await jobApplicationApi.getPublicNeedById(id);
                if (!alive) return;
                setNeed(data);
                setState('ok');
                document.title = `${data.title} — ${data.organisation_name} · Nexilink`;
            } catch {
                if (!alive) return;
                setState('unavailable');
            }
        })();
        return () => { alive = false; };
    }, [id]);

    if (state === 'loading') {
        return <PublicShell><LoadingState message="Laddar annons..." /></PublicShell>;
    }

    if (state === 'unavailable' || !need) {
        return (
            <PublicShell>
                <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-8 md:p-10 text-center`}>
                    <h1 className="text-xl font-bold text-gray-900">Annonsen är inte längre tillgänglig</h1>
                    <p className="mt-2 text-gray-600">
                        Den här tjänsten har antingen tillsatts, pausats eller tagits bort.
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1A4D4F] text-white font-semibold hover:bg-[#143c3e] transition-colors"
                    >
                        Till Nexilink <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </PublicShell>
        );
    }

    // Apply CTA is auth-aware: a candidate applies directly; anyone else is sent to
    // sign in first (with a return path back into the authed apply flow). The public
    // endpoint can't accept an application (that route is authenticated).
    const isCandidate = !!tokens && user?.role === 'candidate';
    const applyFooter = (
        <div className="pt-5 border-t-2 border-gray-100 flex flex-col gap-2">
            {isCandidate ? (
                <ActionButton icon={Send} onClick={() => navigate(`/dashboard/jobs/${need.id}`)}>
                    Ansök till denna tjänst
                </ActionButton>
            ) : !tokens ? (
                <>
                    <ActionButton icon={Send} onClick={() => navigate(`/?redirect=/dashboard/jobs/${need.id}`)}>
                        Logga in för att ansöka
                    </ActionButton>
                    <p className="text-xs text-gray-400">
                        Har du inget konto? Skapa ett kostnadsfritt för att ansöka.
                    </p>
                </>
            ) : (
                // Logged in but not a candidate (e.g. employer) — no apply, just a way in.
                <button
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center gap-2 text-sm text-[#1A4D4F] font-medium hover:underline"
                >
                    Öppna i Nexilink <ArrowRight className="w-4 h-4" />
                </button>
            )}
        </div>
    );

    return (
        <PublicShell>
            {/* Org line above the card — context for a cold visitor arriving from a link. */}
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
                <Building2 className="w-4 h-4 text-[#1A4D4F]" />
                <span className="font-medium text-gray-700">{need.organisation_name}</span>
                {need.unit_name && <span className="text-gray-400">· {need.unit_name}</span>}
            </div>
            <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6 md:p-8`}>
                <NeedView data={publicNeedToView(need)} footer={applyFooter} />
            </div>
        </PublicShell>
    );
};

export default PublicJobPage;
